import type { ReactionType } from "@/react/components/Reactions/Reaction";
import type {
  ChatRoomMessage,
  ChatThreadListItem,
} from "@/react/types/Chat/ChatRoomMessage";
import { useInfiniteQueryHelpers } from "../reactQuery/useInfiniteQueryHelpers";

interface ReactionAttributes {
  community_member_id: number;
  emoji: string;
  reactionable_id: number;
}

export const useChatThreadListReaction = () => {
  const { findAndReplaceRecord } = useInfiniteQueryHelpers();

  const findAndReplaceThread = (thread: any) => {
    findAndReplaceRecord(thread, ["chat-threads-list"]);
  };

  const updateReactionInThreadList = (
    data: any,
    chatThreads: ChatThreadListItem[],
    isAdding: boolean,
  ) => {
    const updateFn = isAdding
      ? createReactionFromMessage
      : removeReactionFromMessage;

    const isParentMessage = !data.parent_message_id;

    if (isParentMessage) {
      const thread = chatThreads.find(
        (thread: ChatThreadListItem) =>
          thread.parent_message.id === data.reactionable_id,
      );

      if (thread) {
        const updatedMessages = updateFn([thread.parent_message], data);
        if (updatedMessages) {
          const updatedThread = {
            ...thread,
            parent_message: updatedMessages[0],
          };
          findAndReplaceThread(updatedThread);
        }
      }
    } else {
      const thread = chatThreads.find(
        (thread: ChatThreadListItem) =>
          thread.parent_message.id === data.parent_message_id,
      );

      if (thread) {
        const updatedReplies = updateFn(thread.replies, data);
        if (updatedReplies) {
          const updatedThread = {
            ...thread,
            replies: updatedReplies,
          };
          findAndReplaceThread(updatedThread);
        }
      }
    }
  };

  const createReactionFromMessage = (
    messages: ChatRoomMessage[],
    reactionAttributes: ReactionAttributes,
  ): ChatRoomMessage[] | undefined => {
    const {
      community_member_id: communityMemberId,
      emoji,
      reactionable_id: reactionableId,
    } = reactionAttributes;

    const updatedMessages: ChatRoomMessage[] = structuredClone(messages);

    const messageIndex = updatedMessages.findIndex(
      (messageItem: ChatRoomMessage) => messageItem.id === reactionableId,
    );

    if (messageIndex === -1) return;

    const message = updatedMessages[messageIndex];
    const existingReactionIndex = message.reactions.findIndex(
      (reaction: ReactionType) => reaction.emoji === emoji,
    );

    if (
      existingReactionIndex !== -1 &&
      message.reactions[existingReactionIndex]?.community_member_ids?.includes(
        communityMemberId,
      )
    ) {
      return;
    }

    if (existingReactionIndex !== -1) {
      message.reactions[existingReactionIndex].community_member_ids.push(
        communityMemberId,
      );
      message.reactions[existingReactionIndex].count += 1;
    } else {
      const newReaction: ReactionType = {
        emoji,
        community_member_ids: [communityMemberId],
        count: 1,
      };
      message.reactions.push(newReaction);
    }

    return updatedMessages;
  };

  const removeReactionFromMessage = (
    messages: ChatRoomMessage[],
    reactionAttributes: ReactionAttributes,
  ): ChatRoomMessage[] | undefined => {
    const {
      community_member_id: communityMemberId,
      emoji,
      reactionable_id: reactionableId,
    } = reactionAttributes;

    const updatedMessages: ChatRoomMessage[] = structuredClone(messages);

    const messageIndex = updatedMessages.findIndex(
      (messageItem: ChatRoomMessage) => messageItem.id === reactionableId,
    );

    if (messageIndex === -1) return;

    const message = updatedMessages[messageIndex];
    const existingReactionIndex = message.reactions.findIndex(
      (reaction: ReactionType) => reaction.emoji === emoji,
    );

    if (
      existingReactionIndex === -1 ||
      !message.reactions[existingReactionIndex]?.community_member_ids?.includes(
        communityMemberId,
      )
    ) {
      return;
    }

    if (message.reactions[existingReactionIndex].count === 1) {
      message.reactions.splice(existingReactionIndex, 1);
    } else {
      message.reactions[existingReactionIndex].community_member_ids =
        message.reactions[existingReactionIndex].community_member_ids.filter(
          (id: number) => id !== communityMemberId,
        );
      message.reactions[existingReactionIndex].count -= 1;
    }
    return updatedMessages;
  };

  const handleReactionFromWebsocket = (
    data: any,
    chatThreads: ChatThreadListItem[],
  ) => {
    if (data.json_message.reactionable_type === "ChatRoomMessage") {
      if (data.event === "createReaction") {
        updateReactionInThreadList(data.json_message, chatThreads, true);
      } else if (data.event === "destroyReaction") {
        updateReactionInThreadList(data.json_message, chatThreads, false);
      }
    }
  };

  return {
    handleReactionFromWebsocket,
  };
};
